import folderIcon from '@/assets/icons/folder-icon.svg'
import { cn } from '@/lib/utils'
import { Clock, Heart, Home, Lock, Share2, Trash2 } from 'lucide-react'
import { FinderSection } from '../hooks/useFinder'
import { formatBytes } from '../utils'

interface SidebarSection {
    id: FinderSection
    label: string
    icon: React.ReactNode
    count?: number
}

interface SystemFolder {
    id: string
    name: string
}

interface FinderSidebarProps {
    activeSection: FinderSection
    onSectionChange: (section: FinderSection) => void
    recentCount: number
    favoritesCount: number
    trashCount: number
    storageUsed: number
    storageTotal: number
    dropTargetId: string | null
    onDragOver: (sectionId: string, e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDrop: (sectionId: string, e: React.DragEvent) => void
    systemFolders?: SystemFolder[]
    onSystemFolderClick?: (folderId: string) => void
}

export function FinderSidebar({
    activeSection,
    onSectionChange,
    recentCount,
    favoritesCount,
    trashCount,
    storageUsed,
    storageTotal,
    dropTargetId,
    onDragOver,
    onDragLeave,
    onDrop,
    systemFolders = [],
    onSystemFolderClick,
}: FinderSidebarProps) {
    const sections: SidebarSection[] = [
        { id: 'all', label: 'All Documents', icon: <Home className='w-4 h-4' /> },
        { id: 'recent', label: 'Recent', icon: <Clock className='w-4 h-4' />, count: recentCount },
        { id: 'favorites', label: 'Favorites', icon: <Heart className='w-4 h-4' />, count: favoritesCount },
        { id: 'shared', label: 'Shared with Me', icon: <Share2 className='w-4 h-4' /> },
        { id: 'trash', label: 'Trash', icon: <Trash2 className='w-4 h-4' />, count: trashCount },
    ]

    const storagePercent = storageTotal > 0 ? Math.round((storageUsed / storageTotal) * 100) : 0

    return (
        <aside className='flex flex-col w-52 h-full border-r bg-gray-50/50 border-gray-100 shrink-0'>
            <div className='flex-1 overflow-y-auto p-4'>
                <nav className='space-y-5'>
                    <div>
                        <h3 className='px-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2'>
                            Navigation
                        </h3>
                        <div className='space-y-0.5'>
                            {sections.map(section => (
                                <button
                                    key={section.id}
                                    onClick={() => onSectionChange(section.id)}
                                    onDragOver={e => {
                                        e.preventDefault()
                                        onDragOver(section.id, e)
                                    }}
                                    onDragLeave={onDragLeave}
                                    onDrop={e => onDrop(section.id, e)}
                                    className={cn(
                                        'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm transition-all',
                                        activeSection === section.id
                                            ? 'bg-[#63316B]/10 text-[#63316B] font-medium'
                                            : 'text-gray-600 hover:bg-gray-100',
                                        dropTargetId === section.id && 'bg-[#63316B] text-white'
                                    )}
                                >
                                    {section.icon}
                                    <span className='flex-1 text-left'>{section.label}</span>
                                    {section.count !== undefined && section.count > 0 && (
                                        <span
                                            className={cn(
                                                'text-[10px] px-1.5 py-0.5 rounded-full',
                                                activeSection === section.id
                                                    ? 'bg-[#63316B]/20 text-[#63316B]'
                                                    : 'bg-gray-200 text-gray-500',
                                                dropTargetId === section.id && 'bg-white/20 text-white'
                                            )}
                                        >
                                            {section.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {systemFolders.length > 0 && (
                        <div>
                            <h3 className='px-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1'>
                                <Lock className='w-3 h-3' />
                                Workspace Library
                            </h3>
                            <div className='space-y-0.5'>
                                {systemFolders.map(folder => (
                                    <button
                                        key={folder.id}
                                        onClick={() => onSystemFolderClick?.(folder.id)}
                                        onDragOver={e => {
                                            e.preventDefault()
                                            onDragOver(folder.id, e)
                                        }}
                                        onDragLeave={onDragLeave}
                                        onDrop={e => onDrop(folder.id, e)}
                                        className={cn(
                                            'w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm transition-all',
                                            'text-gray-600 hover:bg-gray-100',
                                            dropTargetId === folder.id && 'bg-[#63316B] text-white'
                                        )}
                                    >
                                        <img src={folderIcon} alt='' className='w-4 h-4' />
                                        <span className='flex-1 text-left truncate'>{folder.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </nav>
            </div>

            {storageTotal > 0 && (
                <div className='shrink-0 p-4 border-t border-gray-100'>
                    <div className='bg-white rounded-lg p-2.5 border border-gray-100'>
                        <div className='flex justify-between text-[10px] text-gray-400 mb-1.5'>
                            <span>Storage</span>
                            <span>{storagePercent}%</span>
                        </div>
                        <div className='w-full bg-gray-100 rounded-full h-1 overflow-hidden'>
                            <div
                                className='bg-[#63316B] h-1 rounded-full transition-all'
                                style={{ width: `${storagePercent}%` }}
                            />
                        </div>
                        <p className='mt-1 text-[10px] text-gray-400'>
                            {formatBytes(storageUsed)} of {formatBytes(storageTotal)}
                        </p>
                    </div>
                </div>
            )}
        </aside>
    )
}
